import { sectionViewDef, type Crop2D, type Vec3, type View2DDef } from './lib'

// ---------------------------------------------------------------------------
// Corte a partir da CAIXA DE CORTE do viewer.
//
// A caixa é uma OBB: origem + três eixos ortonormais (`SectionOrientation`)
// e extensões locais (`SectionLocalExtents`). Tem seis faces; só uma é
// que interessa como plano de corte.
//
// QUE FACE SE ESCOLHE, E PORQUÊ:
//
//   1. Descartam-se as faces cuja normal é quase vertical (|ny| > 0.7).
//      Uma face horizontal daria uma PLANTA, e para plantas existe o
//      caminho dedicado por piso, com a cota certa e a profundidade
//      certa. Aqui queremos o que o utilizador chama "corte".
//   2. Entre as quatro faces verticais, escolhe-se aquela que está
//      VIRADA PARA A CÂMARA: a que maximiza normal_exterior · (câmara −
//      centro da face). É exactamente a face que o utilizador está a
//      ver quando encolhe a caixa e o modelo aparece cortado. Sem
//      câmara (testes, chamadas sem mundo), escolhe-se de forma
//      determinística a primeira da ordem canónica das faces.
//   3. `viewDir` é a normal INTERIOR dessa face (aponta para dentro da
//      caixa, que é o lado onde está o modelo que se quer desenhar),
//      coerente com a convenção `viewDir = up × right` da fase 1.
//   4. `depth` é a espessura da caixa medida ao longo desse eixo: a
//      vista mostra exactamente o que a caixa mostra.
//   5. O `crop` sai da projecção dos 8 cantos da caixa no referencial
//      (right, up) da vista. Não se assume alinhamento entre os eixos
//      da caixa e o (right, up) do desenho, porque o `up` do corte é
//      sempre a vertical do MUNDO e a caixa pode estar rodada.
//
// Puro: recebe números, devolve uma `View2DDef`. Sem three, sem store.
// ---------------------------------------------------------------------------

export interface BoxOrientation {
  origin: Vec3
  axisX: Vec3
  axisY: Vec3
  axisZ: Vec3
}

export interface BoxExtents {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

export type BoxFaceKey = 'minX' | 'maxX' | 'minY' | 'maxY' | 'minZ' | 'maxZ'

/** Ordem canónica: fixa o desempate quando não há câmara. */
export const BOX_FACE_ORDER: BoxFaceKey[] = [
  'minX',
  'maxX',
  'minZ',
  'maxZ',
  'minY',
  'maxY',
]

/** Acima deste |ny| a face conta como horizontal (≈ 45°) e é
 *  descartada: daria uma planta, não um corte. */
const VERTICAL_FACE_MAX_NY = 0.7

function localToWorldDir(o: BoxOrientation, v: Vec3): Vec3 {
  return [
    v[0] * o.axisX[0] + v[1] * o.axisY[0] + v[2] * o.axisZ[0],
    v[0] * o.axisX[1] + v[1] * o.axisY[1] + v[2] * o.axisZ[1],
    v[0] * o.axisX[2] + v[1] * o.axisY[2] + v[2] * o.axisZ[2],
  ]
}

function localToWorldPoint(o: BoxOrientation, v: Vec3): Vec3 {
  const d = localToWorldDir(o, v)
  return [d[0] + o.origin[0], d[1] + o.origin[1], d[2] + o.origin[2]]
}

function faceLocalNormal(face: BoxFaceKey): Vec3 {
  switch (face) {
    case 'minX':
      return [-1, 0, 0]
    case 'maxX':
      return [1, 0, 0]
    case 'minY':
      return [0, -1, 0]
    case 'maxY':
      return [0, 1, 0]
    case 'minZ':
      return [0, 0, -1]
    case 'maxZ':
      return [0, 0, 1]
  }
}

function faceLocalCentre(e: BoxExtents, face: BoxFaceKey): Vec3 {
  const cx = (e.minX + e.maxX) / 2
  const cy = (e.minY + e.maxY) / 2
  const cz = (e.minZ + e.maxZ) / 2
  switch (face) {
    case 'minX':
      return [e.minX, cy, cz]
    case 'maxX':
      return [e.maxX, cy, cz]
    case 'minY':
      return [cx, e.minY, cz]
    case 'maxY':
      return [cx, e.maxY, cz]
    case 'minZ':
      return [cx, cy, e.minZ]
    case 'maxZ':
      return [cx, cy, e.maxZ]
  }
}

/** Espessura da caixa ao longo do eixo da face. */
function faceDepth(e: BoxExtents, face: BoxFaceKey): number {
  if (face === 'minX' || face === 'maxX') return Math.abs(e.maxX - e.minX)
  if (face === 'minY' || face === 'maxY') return Math.abs(e.maxY - e.minY)
  return Math.abs(e.maxZ - e.minZ)
}

function boxCorners(o: BoxOrientation, e: BoxExtents): Vec3[] {
  const out: Vec3[] = []
  for (const z of [e.minZ, e.maxZ]) {
    for (const y of [e.minY, e.maxY]) {
      for (const x of [e.minX, e.maxX]) {
        out.push(localToWorldPoint(o, [x, y, z]))
      }
    }
  }
  return out
}

export interface PickedBoxFace {
  face: BoxFaceKey
  /** Centro da face, em coordenadas de mundo. */
  origin: Vec3
  /** Normal INTERIOR (para dentro da caixa): a direcção de observação. */
  viewDir: Vec3
  depth: number
}

/** Escolhe a face de corte mais útil. `null` quando a caixa não tem
 *  nenhuma face vertical utilizável (caixa achatada, eixos degenerados). */
export function pickSectionFace(
  orientation: BoxOrientation,
  extents: BoxExtents,
  cameraPos?: Vec3 | null,
): PickedBoxFace | null {
  let best: PickedBoxFace | null = null
  let bestScore = -Infinity
  for (const face of BOX_FACE_ORDER) {
    const outward = localToWorldDir(orientation, faceLocalNormal(face))
    const n = Math.hypot(outward[0], outward[1], outward[2])
    if (!Number.isFinite(n) || n < 1e-9) continue
    const unit: Vec3 = [outward[0] / n, outward[1] / n, outward[2] / n]
    if (Math.abs(unit[1]) > VERTICAL_FACE_MAX_NY) continue
    const depth = faceDepth(extents, face)
    if (!Number.isFinite(depth) || depth <= 1e-6) continue
    const centre = localToWorldPoint(orientation, faceLocalCentre(extents, face))
    let score = 0
    if (cameraPos) {
      const dx = cameraPos[0] - centre[0]
      const dy = cameraPos[1] - centre[1]
      const dz = cameraPos[2] - centre[2]
      const dl = Math.hypot(dx, dy, dz)
      score =
        dl > 1e-9
          ? (unit[0] * dx + unit[1] * dy + unit[2] * dz) / dl
          : 0
    }
    // Sem câmara todos empatam a 0 e ganha o primeiro da ordem
    // canónica (comparação estrita: só substitui se for melhor).
    if (score > bestScore) {
      bestScore = score
      best = {
        face,
        origin: centre,
        viewDir: [-unit[0], -unit[1], -unit[2]],
        depth,
      }
    }
  }
  return best
}

/** Janela do desenho que corresponde à caixa: bbox dos 8 cantos
 *  projectados no referencial (right, up) da vista, com a origem no
 *  ponto do plano de corte. */
export function cropFromBox(
  orientation: BoxOrientation,
  extents: BoxExtents,
  origin: Vec3,
  right: Vec3,
  up: Vec3,
): Crop2D {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const c of boxCorners(orientation, extents)) {
    const dx = c[0] - origin[0]
    const dy = c[1] - origin[1]
    const dz = c[2] - origin[2]
    const x = dx * right[0] + dy * right[1] + dz * right[2]
    const y = dx * up[0] + dy * up[1] + dz * up[2]
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

/** Constrói a vista de corte a partir do estado da caixa de corte.
 *  `null` quando a caixa não dá nenhum corte vertical utilizável. */
export function sectionViewFromBox(opts: {
  orientation: BoxOrientation
  extents: BoxExtents
  name: string
  id?: string
  cameraPos?: Vec3 | null
  scaleDenom?: number
}): View2DDef | null {
  const picked = pickSectionFace(
    opts.orientation,
    opts.extents,
    opts.cameraPos ?? null,
  )
  if (!picked) return null
  let def: View2DDef
  try {
    def = sectionViewDef({
      id: opts.id,
      name: opts.name,
      origin: picked.origin,
      viewDir: picked.viewDir,
      depth: picked.depth,
      scaleDenom: opts.scaleDenom,
    })
  } catch {
    // `sectionViewDef` lança em referencial degenerado (direcção sem
    // componente horizontal). Aqui já filtrámos as faces horizontais,
    // mas uma caixa com eixos estragados ainda pode lá chegar.
    return null
  }
  def.crop = cropFromBox(
    opts.orientation,
    opts.extents,
    def.origin,
    def.right,
    def.up,
  )
  return def
}

/** Azimute (graus a partir do Norte, sentido horário) de uma direcção
 *  de observação horizontal. Inverso do `azimuthToViewDir` da fase 1,
 *  usado para mostrar ao utilizador a orientação de um corte gerado a
 *  partir da caixa. Normalizado a [0, 360). */
export function viewDirToAzimuth(viewDir: Vec3): number {
  const a = (Math.atan2(viewDir[0], -viewDir[2]) * 180) / Math.PI
  return ((a % 360) + 360) % 360
}
